import { GridColumnDefinition, GridColumnOptions, GridSetup, type IGridColumnSort } from '@/components/Grid/Classes';
import MatrixRow from './MatrixRow';
import { RaceContestantTime, ContestantTime, Station, StationRole } from '@/objects/generated';
import GridDataSource, { type IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import { getMatrixGeneralColumns } from './MatrixGeneralColumns';
import MatrixStationRoleCell from './MatrixStationRoleCell';
import MatrixStationRoleCellRenderer from '../SubComponents/MatrixStationRoleCellRenderer.vue';
import { formatNumberHumanReadableTrimZeroes, roundNumberHumanReadableTrimZeroes, arrayUnique, arrayGroupbyToMap } from '@/utilities';
import type IMatrixConfig from './IMatrixConfig';
import {
    addSecondsToDate,
    addTimespanToDate,
    compareDateTimes,
    getCurrentDateFormat,
    parseDateStringIntoDateFromFormat,
    timeDiffAsMilliseconds,
} from '@/utilities/dateutils';
import GridCheckboxSelectionColumnOptions from '@/components/Grid/Classes/GridCheckboxSelectionColumnOptions';
import { ClickIconRenderer } from '@/components';
import { markRaw } from 'vue';
import { CreateSearchFragmentsFromPattern, type SearchStringFragments } from '@/utilities/searchutils';
import { TimeSpan } from '@/objects/TimeSpan';
import type { MatrixDataProvider } from './MatrixDataProvider';
import type {
    BackupStationId,
    CategoryId,
    ContestantId,
    RaceContestantId,
    RaceId,
    StationId,
    StationRoleId,
} from '@/utilities/typealiases';
import type { MatrixContestant } from './MatrixContestant';
import { hasStartControlWarning } from '@/utilities/timingUtils';
import { buildStationRoleColumnLayoutForRaces } from './MatrixStationRoleToColumnLayout.ts';
import { GetExitStatusSortValue } from '@/utilities/utils.ts';
import MatrixStationRoleHeader from '../SubComponents/MatrixStationRoleHeader.vue';
import type { ComponentProps } from 'vue-component-type-helpers';

type MatrixStationRoleCellContextRole = {
    StationRoleId: StationRoleId;
    StationId: StationId;
    Lap: number;
};

type MatrixStationRoleCellContext = {
    IsShooting: boolean;
    Roles: MatrixStationRoleCellContextRole[];
};

type MatrixStationRoleCellLayoutInfo = {
    StationRoleId: StationRoleId;
    Lap: number;
};

export type MatrixStationRoleColumnLayout = {
    Roles: MatrixStationRoleCellLayoutInfo[];
    IsShootingColumn: boolean;
};

const RowMarkedForRemovalMarkerClass = 'grid-cell-success';
const RowMarkedAsNewMarkerClass = 'grid-cell-warning';
export const StationRoleColumnIdPrefix = 'stationrole';

/**
 * Builds and rebuilds the grid setup object for the matrix.
 * @param config Matrix configuration
 * @param stationRoles All the stations in the event, regardless of race.
 * @param categories All the categories in the event.
 * @param groups All the groups in the event.
 * @param rowGetter The rowGetter function to be supplied to the grid.
 * @param matrixContestants We include the matrixContestants so we can find the max rounds from the actual contestants.
 * @returns A GridSetup object useable by the grid component.
 */
export const buildGridSetup = (
    config: IMatrixConfig,
    stationRoles: Map<StationRoleId, StationRole>,
    stations: Map<StationId, Station>,
    rowGetter: (params: IGridRowGetterParams<MatrixRow>) => void,
    matrixContestants: Map<RaceContestantId, MatrixContestant>,
    openContestantDialog: (raceContestantId: RaceContestantId) => void,
    shouldExpectShootingPictures: boolean,
    rowCountGetter: () => number,
    totalRowCountGetter: () => number,
): GridSetup<MatrixRow> | null => {
    if (config.RaceIDs == null) {
        return null;
    }

    const layout = buildStationRoleColumnLayoutForRaces(config.RaceIDs, stationRoles, matrixContestants);

    const hasMultiLaps = Array.from(matrixContestants.values()).some((c) => c.MaxLaps > 1);

    // index of the stationRole columns in the grid. We need this because we are looking up the stationRole by index, which is terrible, but I cant find a better way with ag-grid.
    let stationIndex = 0;

    return new GridSetup<MatrixRow>({
        rowSelection: 'multiple',
        rowIdGetter: (row) => row.RaceContestantId.toString(),
        hideStatusbar: false,
        datasource: new GridDataSource<MatrixRow>({
            rowGetter,
            rowCountGetter: rowCountGetter,
            totalNumberOfRows: totalRowCountGetter,
        }),

        hasCheckboxSelectionColumn: true,

        GridColumnDefinition: new GridColumnDefinition<MatrixRow>({
            CommonColumnOptions: {
                editable: false,
            },
            CheckboxSelectionColumn: new GridCheckboxSelectionColumnOptions<MatrixRow>({
                suppressMovable: true,
                maxWidth: 58,
                minWidth: 58,
                pinned: 'left',
                cellRenderer: markRaw(ClickIconRenderer),
                cellRendererParams: {
                    icon: 'fa-file-pen',
                    action: (val: RaceContestantId) => openContestantDialog(val),
                },
                valueGetter: (row) => row.RaceContestantId,
                cellClassRules: [
                    {
                        className: RowMarkedForRemovalMarkerClass,
                        func: (_, row) => row.MarkedForRemoval,
                    },
                    {
                        className: RowMarkedAsNewMarkerClass,
                        func: (_, row) => row.MarkedAsNew,
                    },
                ],
                cellRefreshTrigger: (row, _) => {
                    if (!row) {
                        return '';
                    }

                    return {
                        markedForRemoval: row.MarkedForRemoval,
                        markedAsNew: row.MarkedAsNew,
                    };
                },
            }),
            ColumnOptions: [
                // General columns, filtered by config.GeneralColumns
                ...getMatrixGeneralColumns().filter(
                    // Filter to the wanted columns
                    (column) => config.GeneralColumns.includes(column.dataField as keyof MatrixRow),
                ),

                // Map the layout. Each element in the layout is one column. It can be multiple stationRoles per column
                ...[...layout.values()].map((col) => {
                    const localStationIndex = stationIndex; // Capture the current index so we can save it to be used in the sortValueGetter
                    stationIndex++;
                    const columnId = getStationRoleCellIdentifierForStationRoleAndLap(col.Roles);
                    return new GridColumnOptions<
                        MatrixRow,
                        MatrixStationRoleCell,
                        any,
                        any,
                        MatrixStationRoleCellContext,
                        Omit<ComponentProps<typeof MatrixStationRoleHeader>, 'grid' | 'column'>
                    >({
                        colId: columnId,
                        dataField: 'StationRoles.' + localStationIndex,
                        headerComponent: MatrixStationRoleHeader,
                        headerComponentParams: {
                            stationRoleIds: arrayUnique(col.Roles.map((d) => d.StationRoleId)),
                            matrixConfig: config,
                        },
                        columnHeaderText: getStationRoleCellHeaderText(col.Roles, hasMultiLaps, stationRoles, stations),
                        cellClassRules: [
                            {
                                className: 'grid-cell-disabled',
                                func: (cell) => cell.IsFillerCell,
                            },
                        ],
                        headerValueGetter: (headerText) => headerText,
                        sortValueGetter: (row, sortDirection) => {
                            const sr = row.StationRoles[localStationIndex];

                            // We always want rows with no time to be sorted after rows with time. This has alot of 9s cause it needs to be bigger than Date.getTime()
                            const missingTimeSortValue = sortDirection === 'Descending' ? -1 : 999_999_999_999_999;

                            if (row.ExitStatus !== 'None' && config.SortExitStatusLast) {
                                // For existstatus rows we get a large number for the exitstatus (from GetExitStatusSortValue), with offset as the missingTimeSortValue
                                // This will ensure that the number is always larger than any normal time
                                // We minus the usedTime from the missingTimeSortValue to make the rows with time have a lower sort number than the ones without time
                                // We use the missingTimeSortValue as 999_999_999_999_999 here regardless of sortDirection, since we always want this at the bottom, regardless of sort order
                                return (
                                    GetExitStatusSortValue(row.ExitStatus, 999_999_999_999_999 - (sr.RaceContestantTimeUsedTime ?? 0)) *
                                    (sortDirection === 'Descending' ? -1 : 1)
                                );
                            }

                            // On start we always sort by clocktime since that is what is always shown there
                            if (sr.IsStart) {
                                return sr.RaceContestantTimeClockTime?.getTime() ?? missingTimeSortValue;
                            }

                            // Otherwise, sort based on the configured TimeDisplay
                            switch (sr.MatrixConfig.TimeDisplay) {
                                case 'SplitTime':
                                    return sr.SplitTime ?? missingTimeSortValue;
                                case 'ClockTime':
                                    return sr.RaceContestantTimeClockTime?.getTime() ?? missingTimeSortValue;
                                case 'UsedTime':
                                case 'TimeAsX':
                                default:
                                    return sr.RaceContestantTimeUsedTime ?? missingTimeSortValue;
                            }
                        },
                        cellRenderer: MatrixStationRoleCellRenderer,
                        pinned: undefined,
                        hidden: !config.StationRoleIDs.includes(columnId),
                        contextData: {
                            IsShooting: col.IsShootingColumn,
                            Roles: col.Roles.map((roleInCell) => {
                                const role = stationRoles.get(roleInCell.StationRoleId)!; // Role MUST exist since we have its ID
                                return {
                                    StationRoleId: role.ID,
                                    StationId: role.StationID,
                                    Lap: roleInCell.Lap,
                                };
                            }),
                        },
                        width: getStationRoleCellWidthPx(config, col.IsShootingColumn, shouldExpectShootingPictures),
                    });
                }),
            ],
        }),
    });
};

/**
 * Calculates the pixel width of a stationRole cell in the matrix
 * @param config The MatrixConfig
 * @param IsShooting Wether it is a shooting station or not
 * @returns The pixel width the cell should have
 */
export const getStationRoleCellWidthPx = (config: IMatrixConfig, IsShooting: boolean, shouldExpectShootingPictures: boolean): number => {
    const warningsWidth = 20;
    if (IsShooting) {
        if (shouldExpectShootingPictures) {
            return 200 + warningsWidth;
        }
        return 135 + warningsWidth;
    }

    if (config.TimeDisplay === 'TimeAsX') {
        return 30 + warningsWidth;
    }

    switch (config.TimeDecimals) {
        case 'TIME':
            return 64 + warningsWidth;
        case 'TIMEMS1':
            return 72 + warningsWidth;
        case 'TIMEMS2':
            return 80 + warningsWidth;
        case 'TIMEMS3':
            return 88 + warningsWidth;
    }
};

const getStationRoleCellHeaderText = (
    data: MatrixStationRoleCellLayoutInfo[],
    hasMultiLaps: boolean,
    stationRoles: Map<StationRoleId, StationRole>,
    stations: Map<StationId, Station>,
): string => {
    if (!hasMultiLaps) {
        const roles = data.map((roleInCell) => stationRoles.get(roleInCell.StationRoleId)!);
        const roleNames = arrayUnique(roles.map((role) => role.Name ?? stations.get(role.StationID)?.Name ?? role.Sort.toString()));
        const kmFragments = arrayUnique(
            roles
                .filter((role) => role.Km)
                .map(
                    (role) =>
                        `${formatNumberHumanReadableTrimZeroes(role.Km * (data.find((d) => d.StationRoleId === role.ID)?.Lap ?? 0))} km`,
                ),
        );
        return roleNames.join(' / ') + ' ' + kmFragments.join(' / ');
    }
    return arrayUnique(
        data.map((roleInCell) => {
            const role = stationRoles.get(roleInCell.StationRoleId)!; // The role MUST exist, since we have its ID

            const nameFragment = role.Name ?? stations.get(role.StationID)?.Name ?? role.Sort.toString();
            const kmFragment = role.Km ? `${formatNumberHumanReadableTrimZeroes(role.Km * roleInCell.Lap)} km` : '';
            const lapFragment = hasMultiLaps ? `(${roleInCell.Lap})` : '';

            return `${nameFragment} ${lapFragment} ${kmFragment}`;
        }),
    ).join(' / ');
};
/**
 * Builds the rows for the matrix.
 * @param matrixContestants The matrix contestants to build rows from.
 * @param config The matrix configuration
 * @param stationRoles All the stationsRoles in the event
 * @param mappedRaceContestantTimes All the RaceContestantTimes we have, mapped to raceContestantID
 * @param mappedContestantTimes All the ContestantTimes we have, mapped to contestantID
 * @param mappedShootingResults All the ShootingResults we have, mapped to raceContestantID
 * @returns An object with rows as an array of MatrixRow objects and raceContestantLookup as a map of raceContestantID to row index
 */
export const buildRows = (
    config: IMatrixConfig,
    matrixContestants: Map<RaceContestantId, MatrixContestant>,
    matrixDataProvider: MatrixDataProvider,
): { rows: MatrixRow[]; raceContestantLookup: Map<RaceContestantId, MatrixRow>; contestantLookup: Map<ContestantId, MatrixRow[]> } => {
    const filteredContestants = [...matrixContestants.values()].filter((c) => config.RaceIDs?.includes(c.RaceID));
    const stationRolesMap = matrixDataProvider.GetStationRolesMap();
    const layout = buildStationRoleColumnLayoutForRaces(config.RaceIDs ?? [], stationRolesMap, matrixContestants);

    const raceContestantLookup = new Map<RaceContestantId, MatrixRow>();
    const contestantLookup = new Map<ContestantId, MatrixRow[]>();

    // Map the MatrixContestants to MatrixRows
    const filteredRows = filteredContestants.map((c) => {
        const matrixRow = new MatrixRow({
            RaceContestantId: c.RaceContestantID,
            ContestantId: c.ContestantID,
            Bib: c.Bib || 0,
            ExitStatus: c.ExitStatus,
            ExitStatusFlagTime: c.ExitStatusFlagTime,
            ExitStatusReason: c.ExitStatusReason,
            StartTime: c.Starttime,
            ContestantName: c.ContestantName || '',
            TeamName: c.TeamName,
            RaceID: c.RaceID,
            GroupID: c.GroupID,
            GroupName: c.GroupName!,
            CategoryID: c.CategoryID,
            CategoryName: c.CategoryName!,
            MaxLap: c.MaxLaps,
            HasStartGPI: false, // Initalize to false, this will be set by the MatrixStartGpiWarningHandler
            MatrixConfig: config,
            StationRoles: layout.map((layoutColumn) => {
                const columnId = getStationRoleCellIdentifierForStationRoleAndLap(layoutColumn.Roles);
                const matchingRoleInColumn = layoutColumn.Roles.find((roleInColumn) => {
                    const role = stationRolesMap.get(roleInColumn.StationRoleId);
                    if (!role) {
                        return false;
                    }

                    const roleIsForContestantRace = role.RaceID === null || role.RaceID === c.RaceID;
                    const roleLapIsValidForContestant = roleInColumn.Lap <= c.MaxLaps;

                    return roleIsForContestantRace && roleLapIsValidForContestant;
                });

                if (!matchingRoleInColumn) {
                    return new MatrixStationRoleCell({
                        MatrixConfig: config,
                        StationRoleId: -1,
                        ColumnId: columnId,
                        StationID: -1,
                        IsStart: false,
                        IsStartControl: false,
                        LapNumber: 1,
                        IsShooting: false,
                        IsFillerCell: true,
                    });
                }

                const role = stationRolesMap.get(matchingRoleInColumn.StationRoleId)!;

                return new MatrixStationRoleCell({
                    MatrixConfig: config,
                    StationRoleId: role.ID,
                    ColumnId: columnId,
                    StationID: role.StationID,
                    IsStart: role.Start,
                    IsStartControl: role.StartControl,
                    LapNumber: matchingRoleInColumn.Lap,
                    IsShooting: role.IsShootingStation,
                    IsFillerCell: false,
                });
            }),
        });

        raceContestantLookup.set(c.RaceContestantID, matrixRow);

        if (!contestantLookup.has(c.ContestantID)) {
            contestantLookup.set(c.ContestantID, []);
        }
        contestantLookup.get(c.ContestantID)!.push(matrixRow);

        return matrixRow;
    });

    // Add the activetimes to each row
    // for (const raceContestantID of mappedRaceContestantTimes.keys()) {
    for (const raceContestantID of matrixDataProvider.GetRaceContestantIdsWithRaceContestantTimes()) {
        if (!raceContestantLookup.has(raceContestantID)) {
            continue;
        }

        const times = matrixDataProvider.GetRaceContestantTimesForRaceContestantId(raceContestantID);

        if (!times || times.length === 0) {
            continue;
        }

        // We must have times here, and the ContestantID will be the same on each time since we are iterating over the same raceContestantID
        const contestantID = times.at(0)!.ContestantID;

        addRaceContestantTimesToRows(
            raceContestantID,
            times,
            raceContestantLookup,
            matrixDataProvider.GetStationRolesMap(),
            matrixDataProvider.GetContestantTimesFromContestantId(contestantID),
        );
    }

    // Add the rawtimes to each row
    for (const contestantID of matrixDataProvider.GetContestantIdsWithContestantTimes()) {
        if (!contestantLookup.has(contestantID)) {
            continue;
        }

        const times = matrixDataProvider.GetContestantTimesFromContestantId(contestantID)!;

        addContestantTimesToRows(
            contestantID,
            times,
            times,
            matrixDataProvider.GetRaceContestantTimesForContestantId(contestantID),
            contestantLookup,
            matrixDataProvider.GetStationsMap(),
            matrixDataProvider.GetStationRolesMap(),
            matrixDataProvider.GetBackupStationIds(),
            matrixDataProvider.GetStationIdToBackupStationIdsMap(),
            matrixDataProvider.GetCategoriesThatUsesShootingPenaltyTime(),
            matrixDataProvider.GetStationIdsThatArePenaltyLapStations(),
        );
    }

    return { rows: filteredRows, raceContestantLookup, contestantLookup };
};

/**
 * Adds the racecontestant times to the rows.
 *
 * This needs the contestantTimes to be handled already if the jumpstart warning is to be calculated correctly
 *
 * In some cases we need to recalculate the ContestantTimes when we receive a RaceContestantTime
 *
 * Specifically if we are adding a time where we have a backuptime, the backuptime should move to the next fitting cell
 *
 * Or if we are deleting a time, the backuptime might move backwards, so we need to handle the ContestantTimes again in that case as well
 * @param raceContestantID The raceContestantID the times belong to
 * @param raceContestantTimes The ActiveTimes to add
 * @param collection The row collection to add the times to
 * @param raceContestantLookup Lookup of all raceContestants to row index
 * @param stationRoles All the stationRoles of the event so we can lookup data from the role itself
 * @param knownContestantTimes All the ContestantTimes of the event so we can lookup data from the times themselves
 * @returns The stationIds where we either deleted or added a time. These will have to be recalculated with regards to the ContestantTimes
 */
export const addRaceContestantTimesToRows = (
    raceContestantID: RaceContestantId,
    raceContestantTimes: RaceContestantTime[],
    raceContestantLookup: Map<RaceContestantId, MatrixRow>,
    stationRoles: Map<StationRoleId, StationRole>,
    knownContestantTimes: ContestantTime[],
): StationId[] => {
    const updateContestantTimesOnStationIds: StationId[] = [];

    const addStationThatNeedsUpdatesOnContestantTimes = (stationId: StationId) => {
        if (!updateContestantTimesOnStationIds.includes(stationId)) {
            updateContestantTimesOnStationIds.push(stationId);
        }
    };

    // Skip raceContestants that are not currently shown in the matrix
    if (!raceContestantLookup.has(raceContestantID)) {
        return updateContestantTimesOnStationIds;
    }

    const row = raceContestantLookup.get(raceContestantID)!;

    raceContestantTimes.forEach((raceContestantTime) => {
        let cell, cellIndex;
        // We probably dont need this try-catch, but just in case
        try {
            const { cell: innerCell, cellIndex: innerCellIndex } = row.GetStationRoleCellByStationRoleAndLapNumber(
                raceContestantTime.StationRoleID,
                raceContestantTime.LapNumber,
            );
            cell = innerCell;
            cellIndex = innerCellIndex;
        } catch (e) {
            console.error(`Couldn't find cell for time with id ${raceContestantTime.Id} and time ${raceContestantTime.ClockTime}`, e);
            return;
        }

        if (cell.RaceContestantTimeId == raceContestantTime.Id) {
            // Reset the cell and row time data if we have deleted the time
            if (raceContestantTime.Status === 'Deleted') {
                // This is difficult to reset since we dont really know what set it
                // We assume that there is only one start, so if the cell is the start we can safely reset the value on the row
                if (cell.IsStart) {
                    row.HasStartPass = false;
                }

                // The cell keeps track internally if it set the finish time, so we can reset that here
                // This also assumes there is only one finish
                if (cell.HasFinishTime) {
                    cell.HasFinishTime = false;
                    row.HasFinishTime = false;
                }

                // The rest is cell internal, so the cell can handle it itself
                cell.resetRaceContestantTime();

                addStationThatNeedsUpdatesOnContestantTimes(cell.StationID!);

                // When we remove a time, the split and speed on the NEXT cell will be wrong, so we need to reset
                const nextCell = row.StationRoles.at(cellIndex + 1);
                if (nextCell) {
                    nextCell.resetSplitAndSpeed();
                }

                return;
            }
        }

        // We could have the stationRoles as a lookup which would make this (count of stationroles) times faster
        // const stationRole = stationRoles.find((role) => role.ID == raceContestantTime.StationRoleID || role.ID == null);
        const stationRole =
            stationRoles.get(raceContestantTime.StationRoleID) ?? Array.from(stationRoles.values()).find((role) => role.ID == null);

        if (!stationRole) {
            console.warn("Couldn't find stationRole for time:", raceContestantTime, 'This should never happen');
            return;
        }

        // If the cell doesnt already have a time, we are adding a new time, so we need to handle the contestantTimes for this contestant and station
        if (!cell.hasRaceContestantTime()) {
            addStationThatNeedsUpdatesOnContestantTimes(cell.StationID!);
        }

        updateRowHasStartPass(row, stationRole);

        updateRowFinishTime(row, stationRole, cell, raceContestantTime);

        updateCellUsedTime(cell, raceContestantTime);

        updateCellStartControlForRaceContestantTime(row, cell, raceContestantTime, stationRole);

        updateCellJumpStartWarning(cell, raceContestantTime, stationRole, knownContestantTimes);

        // Calculate speed
        if (cellIndex == 0) {
            return;
        }

        const previousCell = row.StationRoles[cellIndex - 1];
        const previousStationRole = stationRoles.get(previousCell.StationRoleId);

        if (!previousStationRole) {
            console.warn("Couldn't find previous stationRole for time:", raceContestantTime, 'This should never happen');
            return;
        }

        setCellSpeed(cell, stationRole, previousStationRole, previousCell);

        setCellSplitTime(cell, previousCell);
    });

    return updateContestantTimesOnStationIds;
};

/**
 * Handles updating the cells when we get contestanttimes
 *
 * In some cases we need to recalculate the RaceContestantTimes when we receive a ContestantTime
 *
 * Specifically in the case where we need to change the jumpstart warning. This is handled in the callsite for now
 * @param contestantId The contestantID the times belong to
 * @param timesNow The new times we just received over signalR
 * @param timesAll ALL the contestantTimes for the contestant
 * @param contestantLookup Lookup of the contestantID to rows (one contestant can have more than one racecontestant, which will make several rows in the matrix)
 * @param stations All the stations in the event
 * @param stationRoles All the stationRoles of the event so we can lookup data from the roles themselves (TODO: This should really be a map)
 * @param backupStationIds The stationIds that are backup stations
 * @param stationIdToBackupStationIdMap A map of stationId to backupStationId, used to find the backup station for a station when we get a new time on that station
 * @returns void
 */
export const addContestantTimesToRows = (
    contestantId: number,
    timesNow: ContestantTime[],
    timesAll: ContestantTime[],
    knownRaceContestantTimes: RaceContestantTime[],
    contestantLookup: Map<number, MatrixRow[]>,
    stations: Map<StationId, Station>,
    stationRoles: Map<StationRoleId, StationRole>,
    backupStationIds: StationId[],
    stationIdToBackupStationIdMap: Map<StationId, BackupStationId[]>,
    categoriesThatUsesShootingPenaltyTime: Set<CategoryId>,
    stationIdsThatArePenaltyLapStations: Set<StationId>,
) => {
    // Note: this is not recalculated on changes of the activetimes, maybe we also need to do that

    const rows = contestantLookup.get(contestantId);

    if (!rows) {
        return;
    }

    updateCellPenaltyLapCount(rows, timesAll, categoriesThatUsesShootingPenaltyTime, stationIdsThatArePenaltyLapStations);

    updateCellBackupTime(rows, timesAll, knownRaceContestantTimes, backupStationIds, stationIdToBackupStationIdMap);

    updateCellContestantTime(rows, timesAll);

    updateCellStartControlForContestantTime(rows, stationRoles);
};

const updateCellStartControlForContestantTime = (rows: MatrixRow[], stationRoles: Map<StationRoleId, StationRole>) => {
    rows.forEach((row) => {
        // If we dont have a starttime, we cant show any startcontrol warning
        const startTime = row.StartTime;
        if (!startTime) {
            return;
        }

        // Find the cell that is startcontrol
        const startControlCell = row.StationRoles.find((sr) => sr.IsStartControl);
        if (!startControlCell) {
            return;
        }

        // Find the last red time on the startcontrol station
        const lastTime = startControlCell.RaceContestantTimeClockTime ?? startControlCell.ContestantClockTime;
        if (!lastTime) {
            return;
        }

        // Find the stationrole.
        const stationRole = stationRoles.get(startControlCell.StationRoleId);
        if (!stationRole) {
            // This will never happen, just a guard
            return;
        }

        // check if we should show a warning for this time
        startControlCell.HasStartControlWarning = hasStartControlWarning(startTime, stationRole, lastTime);
    });
};

/**
 * Calculates the number of penalty laps for each shooting cell
 * @param rows The rows for the contestant the times belong to
 * @param times All the contestantTimes for the contestant
 * @param stations The stations in the event
 */
const updateCellPenaltyLapCount = (
    rows: MatrixRow[],
    times: ContestantTime[],
    categoriesThatUsesShootingPenaltyTime: Set<CategoryId>,
    stationIdsThatArePenaltyLapStations: Set<StationId>,
) => {
    if (stationIdsThatArePenaltyLapStations.size === 0) {
        return;
    }
    // This assumes we only have one strafferunde station in the event.
    const penaltyLapTimes = times.filter((t) => stationIdsThatArePenaltyLapStations.has(t.StationID!));

    // group the times by stationid and time, if the time is witin 5 seconds of eacheother, its the same penalty round
    // The justification for the 5 seconds is that the contestant might have multiple chips, and a penalty round is never as short as 5 seconds
    const groupedPenaltyLapTimes = penaltyLapTimes.reduce(
        ({ acc, prev }, time) => {
            if (acc.length === 0) {
                acc.push(time);
                return { acc, prev: time };
            }

            if (timeDiffAsMilliseconds(prev!.ClockTime, time.ClockTime)! > 5000) {
                acc.push(time);
            }

            return { acc, prev: time };
        },
        { acc: [], prev: undefined } as { acc: ContestantTime[]; prev: ContestantTime | undefined },
    ).acc;

    rows.forEach((row) => {
        const shootingCells = row.StationRoles.filter((c) => c.IsShooting);

        shootingCells.forEach((shootingCell, i) => {
            // If we dont have a time on the shooting, then we also dont have any penalty laps
            // TODO: The shooting time might come after the shootings, we should recalculate in those cases.
            if (!shootingCell.RaceContestantTimeClockTime) {
                return;
            }

            /**
             * The time until we should stop counting penalty laps for this shooting
             */
            const penaltyLapTimeCutoff =
                // The next shooting
                shootingCells.at(i + 1)?.RaceContestantTimeClockTime ??
                // Or the finish time
                row.StationRoles.find((sr) => sr.HasFinishTime)?.RaceContestantTimeClockTime ??
                // Or 5 minutes after the shooting
                addSecondsToDate(shootingCell.RaceContestantTimeClockTime, 5 * 60);

            shootingCell.PenaltyLapCount = groupedPenaltyLapTimes.filter(
                (plt) => plt.ClockTime > shootingCell.RaceContestantTimeClockTime! && plt.ClockTime < penaltyLapTimeCutoff,
            ).length;

            // We update the PenaltyLapCount so we need to check if we have warnings now
            updateCellShootingWarnings(shootingCell, categoriesThatUsesShootingPenaltyTime.has(row.CategoryID) ?? false);
        });
    });
};

export const updateCellShootingWarnings = (cell: MatrixStationRoleCell, categoryHasPenaltySecondsInsteadOfLaps: boolean) => {
    // We havent shot yet, so we should not have any warnings
    if (cell.ShootingResult === null) {
        cell.HasPenaltyLapWarning = false;
        cell.HasBackupMissesWarning = false;
        return;
    }

    const misses = cell.ShootingResult.Misses.filter((m) => m === true).length;

    // Check backupMisses
    if (cell.BackupShootingMisses !== null) {
        // null means no backup exists, so we can skip the check
        if (cell.BackupShootingMisses !== misses) {
            cell.HasBackupMissesWarning = true;
        }
    }

    // If we are using time penalty, we will never have a penalty lap warning, so we can skip that check
    if (categoryHasPenaltySecondsInsteadOfLaps) {
        cell.HasPenaltyLapWarning = false;
        return;
    }

    // Check penalty laps
    if (cell.PenaltyLapCount !== misses) {
        cell.HasPenaltyLapWarning = true;
    } else {
        cell.HasPenaltyLapWarning = false;
    }
};

/**
 * Sets the backuptime for each cell (if any)
 * TODO: This now gets all the times for contestant. If I think about it hard enough I can probably fix this so we only need the new times
 * Basically this means we cant reset anything else than the times we got this time. I'm not sure if this is actually possible, but I should think about it for abit
 * @param rows The rows for the contestant the times belong to
 * @param times The times we just received for the contestant
 * @param stations The stations in the event
 * @param knownRaceContestantTimes All the RaceContestantTimes of the event so we can lookup data from the times themselves
 * @param backupStationIds The stationIds that are backup stations
 * @param stationIdToBackupStationIdsMap A map of stationId to backupStationId
 */
const updateCellBackupTime = (
    rows: MatrixRow[],
    times: ContestantTime[],
    knownRaceContestantTimes: RaceContestantTime[],
    backupStationIds: StationId[],
    stationIdToBackupStationIdMap: Map<StationId, BackupStationId[]>,
) => {
    // If no backup stations we are done
    if (backupStationIds.length === 0) {
        return;
    }

    const allBackupTimes = times.filter((t) => t.StationID !== null && backupStationIds.includes(t.StationID));

    if (allBackupTimes.length === 0) {
        return;
    }

    rows.forEach((row) => {
        let contestantBackupTimes = allBackupTimes.filter(
            (bt) => bt.ContestantID === row.ContestantId && bt.ClockTime > (row.StartTime ?? new Date(0)) && bt.StationID !== null,
        );

        if (contestantBackupTimes.length === 0) {
            // Since we have no backuptimes we reset any cells that have backuptimes
            row.StationRoles.filter((c) => c.hasBackupTime()).forEach((c) => c.resetBackupTime());
            return;
        }

        // We only care about cells that have a backup station
        const cellsToUpdate = row.StationRoles.filter((c) => stationIdToBackupStationIdMap.has(c.StationID));

        if (cellsToUpdate.length === 0) {
            return;
        }

        cellsToUpdate.forEach((cell) => {
            // If this now has a raceContestantTime, we should reset the backup and go to the next cell
            if (cell.hasRaceContestantTime()) {
                cell.resetBackupTime();
                return;
            }

            // If we are handling a ContestantTime before the RaceContestantTimes have been handled we need to check if the time is in the knownRaceContestantTimes
            if (knownRaceContestantTimes.some((rct) => rct.StationRoleID === cell.StationRoleId)) {
                cell.resetBackupTime();
                return;
            }

            // Find the cutoff time. Any times before this time cant be used as backuptimes for this cell
            // Basically this is the last known time
            const { cellIndex: stationRoleIndex } = row.GetStationRoleCellByStationRoleAndLapNumber(cell.StationRoleId, cell.LapNumber);

            // Find the time from the previous cell that has a raceContestantTime
            let cutoffTime = row.StationRoles.slice(0, stationRoleIndex)
                .reverse()
                .find((c) => c.hasRaceContestantTime())?.RaceContestantTimeClockTime;

            // If we didnt find any in the row, we look in the knownRaceContestantTimes (this is if we havent applied the RaceContestantTimes to the rows yet)
            if (cutoffTime == null && knownRaceContestantTimes.length > 0) {
                const stationRolesBefore = row.StationRoles.slice(0, stationRoleIndex).map((sr) => sr.StationRoleId);
                // We assume we have the correct sorting here (please god)
                cutoffTime = knownRaceContestantTimes
                    .map((x) => x)
                    .reverse()
                    .find((rct) => stationRolesBefore.includes(rct.StationRoleID) && rct.LapNumber <= cell.LapNumber)?.ClockTime;
            }

            // If we still havent found one, fallback to the starttime or earlier
            if (cutoffTime == null) {
                cutoffTime = row.StartTime ?? new Date(0);
            }

            const backupStationIds = stationIdToBackupStationIdMap.get(cell.StationID!)!;

            // Find the backuptime that fits this cell
            const backupTime = contestantBackupTimes.find((bt) => backupStationIds.includes(bt.StationID!) && bt.ClockTime > cutoffTime);

            if (!backupTime) {
                // Reset the backuptime if we have one, cause we didnt find one this time
                if (cell.hasBackupTime()) {
                    cell.resetBackupTime();
                }
                return;
            }

            // Remove the backuptime we just used from the collection so it does not fit any later cells
            contestantBackupTimes = contestantBackupTimes.filter((bt) => bt !== backupTime);

            // Set the backuptime on the cell
            cell.BackupClockTime = backupTime.ClockTime;
            cell.BackupTimeId = backupTime.Id;
        });
    });
};

const updateCellContestantTime = (rows: MatrixRow[], times: ContestantTime[]) => {
    rows.forEach((row) => {
        const rowStartTimeMinusOneMinute = addSecondsToDate(row.StartTime ?? new Date(0), 60 * 1000 * -1);
        // All the times the row
        const contestantTimes = times.filter(
            (t) => t.ContestantID === row.ContestantId && t.ClockTime > rowStartTimeMinusOneMinute && t.StationID !== null,
        );

        // We want to know what the next RaceContestantTime we have for each cell so we can skip the ContestantTimes that are not valid
        const timeOnNextCellMap: Map<number, Date | null> = new Map(row.StationRoles.map((_sr, index) => [index, null]));
        let lastFilledIndex = 0;
        row.StationRoles.forEach((sr, i) => {
            if (sr.RaceContestantTimeClockTime !== null) {
                for (let j = lastFilledIndex; j < i; j++) {
                    if (timeOnNextCellMap.get(j) === null) {
                        timeOnNextCellMap.set(j, sr.RaceContestantTimeClockTime);
                        lastFilledIndex = j;
                    }
                }
            }
        });

        const stationTimeMap = arrayGroupbyToMap(contestantTimes, (t) => t.StationID!);

        for (const [stationId, timesForStation] of stationTimeMap.entries()) {
            let localTimes = [...timesForStation];

            let cellIndex = -1;
            for (const cell of row.StationRoles) {
                cellIndex++;
                if (cell.hasRaceContestantTime()) {
                    // Remove times that are before known raceContestantTimes, and the time with the same tuid as we have just used
                    const cutoffTime = addTimespanToDate(TimeSpan.FromSeconds(10), cell.RaceContestantTimeClockTime!);
                    localTimes = localTimes.filter((t) => t.ClockTime >= cutoffTime!).filter((t) => t.TUID !== cell.RaceContestantTimeTUID);
                    // If we ran out of times, we are done here
                    if (localTimes.length === 0) {
                        break;
                    }
                    continue;
                }

                // Skip stations that are not the current station
                if (cell.StationID !== stationId) {
                    continue;
                }

                let timeToUse: ContestantTime | undefined;
                // If its startcontrol we want the last time we know about
                if (cell.IsStartControl) {
                    // We dont want to go past the time in the next cell
                    const timeOnNext = timeOnNextCellMap.get(cellIndex);
                    // So if we know what our next time is, thats our cutoff
                    if (timeOnNext) {
                        timeToUse = localTimes.filter((t) => t.ClockTime < timeOnNext).at(-1);
                    } else {
                        // If we dont have a next time, we use the very last one
                        timeToUse = localTimes.at(-1);
                        localTimes = []; // Since we now have used all the times we can clear the array
                    }
                } else {
                    // Otherwise we get the first
                    timeToUse = localTimes.at(0)!;
                }

                // We didnt find a timeToUse
                if (!timeToUse) {
                    continue;
                }

                // Remove any times before the one we placed now. Later cells cant get an earlier time
                localTimes = localTimes.filter((t) => t.ClockTime >= addTimespanToDate(TimeSpan.FromSeconds(10), timeToUse.ClockTime)!);

                cell.ContestantTimeId = timeToUse.Id;
                cell.ContestantClockTime = timeToUse.ClockTime;

                // TODO: We might want to break here so we only ever put a red time in the next cell for any given station, this will make less noise in the matrix

                // If we ran out of times, we are done here
                if (localTimes.length === 0) {
                    break;
                }
            }
        }
    });
};

/**
 * Filters MatrixRows based on the current filters
 * @param rows The rows we want to filter
 * @param config The current config that contains the filter settings
 * @returns A filtered collection of rows
 */
export const filterMatrixRows = (rows: MatrixRow[], config: IMatrixConfig) => {
    const nowDate = new Date();

    const searchFragments = CreateSearchFragmentsFromPattern(config.SearchString);

    const filteredRows = rows.filter((row) => {
        // If we have a search, we ignore all other filters and show only what you have searched for
        if (config.SearchString.length) {
            return rowFitsSearch(row, searchFragments);
        }

        // If the row has been marked for removal, then lets not filter it here regardless of the other filters
        // It will be removed when the time comes
        if (row.MarkedForRemoval) {
            return true;
        }

        if (config.FilterToMissingTimes && !rowHasMissingTimes(row, config)) {
            return false;
        }

        // Hide DNX and FilterToInTrack removes rows with ExitStatus
        if (config.FilterHideDNX || config.FilterToInTrack) {
            if (row.ExitStatus !== 'None') {
                return false;
            }
        }

        if (config.FilterToInTrack && !rowIsInTrack(row, nowDate)) {
            return false;
        }

        // TODO: We should maybe split the warnings into different filters so you can skip the startcontrol and only see the speed.
        // This also ignores the GPI thing currently. That should be added.
        if (
            config.FilterToWarnings &&
            !rowHasSpeedWarning(row, config) &&
            !rowHasStartControlWarning(row) &&
            !rowHasJumpstartWarning(row) &&
            !rowHasShootingWarnings(row)
        ) {
            return false;
        }

        if (!config.RaceGroupIDs.includes(row.GroupID)) {
            return false;
        }

        if (!config.CategoryIDs.includes(row.CategoryID)) {
            return false;
        }

        return true;
    });

    return filteredRows;
};

/**
 * Checks if a row fits a search string
 * We check the bibs, the contestantname and the teamname.
 * @param row The row we want to check
 * @param searchString The search string we want to check against
 * @returns True if the row fits the search string, false otherwise
 */
const rowFitsSearch = (row: MatrixRow, searchFragments: SearchStringFragments): boolean =>
    searchFragments.numbers.has(row.Bib) ||
    searchFragments.strings.some((s) => row.ContestantName.toLowerCase().includes(s) || (row.TeamName?.toLowerCase().includes(s) ?? false));

/**
 * Checks if any of the cells in a row has a speed warning
 * @param row The row we want to check
 * @param config The IMatrixConfig where we can find the current speedlimit
 * @returns true if any of the cells in the row has a speed warning, false otherwise
 */
const rowHasSpeedWarning = (row: MatrixRow, config: IMatrixConfig): boolean =>
    row.StationRoles.some((role) => role.SpeedKph > config.SpeedLimitKph);

/**
 * Checks if any of the cells in a row has a start control warning
 * @param row The row we want to check
 */
const rowHasStartControlWarning = (row: MatrixRow): boolean => row.StationRoles.some((role) => role.HasStartControlWarning);

/**
 * Checks if any of the cells in a row has a jumpstart warning
 * @param row The row we want to check for jumpstart warnings
 * @returns true if there is a jumpstart warning in the row, false otherwise
 */
const rowHasJumpstartWarning = (row: MatrixRow): boolean => row.StationRoles.some((role) => role.HasJumpStartWarning);

/**
 * Checks if any of the cells in the row has either a penalty lap warning or a backup misses warning
 * @param row The row we want to check for shooting warnings
 * @returns true if there is a shooting warning in the row, false otherwise
 */
const rowHasShootingWarnings = (row: MatrixRow): boolean =>
    row.StationRoles.some((role) => role.HasPenaltyLapWarning || role.HasBackupMissesWarning);

/**
 * Checks if a row is in track
 * @param row The row we want to check
 * @param nowDate A date to check against the starttime. This is so we dont have to create a new date for every row, but we can supply one we have already created. If not supplied, we create a new Date to check against
 * @returns True if the row has started but not finished, false otherwise
 */
const rowIsInTrack = (row: MatrixRow, nowDate?: Date): boolean =>
    row.StartTime != null && row.StartTime < (nowDate ?? new Date()) && !row.HasFinishTime;

/**
 * Checks if a row has any missing times
 * @param row Checks if the row has any missing times
 * @returns true if we are missing any times, false otherwise
 */
const rowHasMissingTimes = (row: MatrixRow, config: IMatrixConfig): boolean =>
    row.StationRoles.filter((rsr) => !rsr.IsFillerCell)
        // Only columns that are currently displayed
        .filter((rsr) => config.StationRoleIDs.includes(rsr.ColumnId))
        // Only roles that has a lap that the row will do (This can happen if the men are doing 10 rounds and the women are doing 8 rounds)
        .filter((role) => role.LapNumber <= row.MaxLap)
        // Do we have any that are missing a time
        .some((role) => !role.hasRaceContestantTime());

/**
 * Gets a unique identifier for a station role cell in the matrix based on an array of stationRole + lap objects
 * @param data The stationRole + lap for each checkpoint this column will represent
 * @returns A unique identifier string
 */
export const getStationRoleCellIdentifierForStationRoleAndLap = (data: MatrixStationRoleCellLayoutInfo[]): string =>
    `${StationRoleColumnIdPrefix}-${data.map((d) => `${d.StationRoleId}-${d.Lap}`).join('-')}`;
/**
 * Checks if the grid needs to be resorted after adding times
 * @param columnSort The previous column sort
 * @param addedTimes The times that were added
 * @returns true if we need to resort the grid, false if we don't
 */
export const checkIfWeNeedToResortOnNewTime = (
    columnSort: IGridColumnSort[],
    addedTimes: RaceContestantTime[],
    getGridColumnFromColumnId: (columnId: string) => GridColumnOptions<MatrixRow> | undefined,
): boolean => {
    // If we have no sort, we don't need to do anything
    if (columnSort.length == 0) {
        return false;
    }

    // TODO: There is probably a better way to do this.
    // Here we both fetch the columns again and again, and we loop through all the times
    // Probably we can check the sort when we are handling the time, instead of checking afterwards.

    // Find all the actual columns that we have sorted on, and filter them down to only the stationRole columns
    const sortedColumns: Array<GridColumnOptions<MatrixRow, MatrixStationRoleCell, unknown, unknown, MatrixStationRoleCellContext>> =
        columnSort
            .filter((cs) => cs.columnId.startsWith(StationRoleColumnIdPrefix))
            .map((sort) => getGridColumnFromColumnId(sort.columnId))
            .filter((col) => col !== undefined);

    // Go through each time and check if that time belongs in a sorted column. If we find one, we need to re-sort
    for (const time of addedTimes) {
        if (
            sortedColumns.some((col) =>
                col?.contextData?.Roles.some((role) => role.StationRoleId === time.StationRoleID && role.Lap == time.LapNumber),
            )
        ) {
            return true;
        }
    }

    return false;
};

/**
 * Orders the list ascending (with copy so we dont modify the original array) and joins the raceIds with a dash to create a unique key for the raceIds
 * @param raceIds The raceIds we want a key for
 * @returns A matrix RaceKey
 */
export const buildRaceKeyFromRaceIds = (raceIds: RaceId[]): string => {
    return [...raceIds].sort().join('-');
};

/**
 * Converts a RaceKey back to an array of RaceIds
 * Note: The raceKey is already saved in the MatrixConfig so prefer to use that instead of recalculating if possible
 * @param raceKey A matrix RaceKey
 * @returns An array of RaceIds from the raceKey
 */
export const getRaceIdsFromRaceKey = (raceKey: string): RaceId[] => {
    return raceKey.split('-').map((id) => parseInt(id));
};

const updateRowHasStartPass = (row: MatrixRow, stationRole: StationRole) => {
    if (row.HasStartPass) {
        return;
    }

    if (!stationRole.Start) {
        return;
    }

    row.HasStartPass = true;
};

const updateRowFinishTime = (row: MatrixRow, stationRole: StationRole, cell: MatrixStationRoleCell, time: RaceContestantTime) => {
    if (row.HasFinishTime) {
        return;
    }

    if (!stationRole.Finish) {
        return;
    }

    if (time.LapNumber != row.MaxLap) {
        return;
    }

    cell.HasFinishTime = true;
    row.HasFinishTime = true;
};

const setCellSpeed = (
    cell: MatrixStationRoleCell,
    stationRole: StationRole,
    previousStationRole: StationRole,
    previousCell: MatrixStationRoleCell,
) => {
    // Cant calculate speed if we don't have km
    if (!stationRole?.Km) {
        return;
    }

    // If the cell has no time, we can't calculate speed
    if (cell.RaceContestantTimeUsedTime == null) {
        return;
    }

    // If the previous cell has no time, we can't calculate speed
    if (previousCell.RaceContestantTimeUsedTime == null) {
        return;
    }

    // If the previous stationRole has no km, we can't calculate speed (0 is allowed since it may be a start station at km 0)
    if (previousStationRole.Km === null || previousStationRole.Km === undefined) {
        return;
    }

    cell.SpeedKph = roundNumberHumanReadableTrimZeroes(
        ((stationRole.Km ?? 0) - (previousStationRole.Km ?? 0)) /
            ((cell.RaceContestantTimeUsedTime - previousCell.RaceContestantTimeUsedTime) / 1000 / 60 / 60),
    );
};

const setCellSplitTime = (cell: MatrixStationRoleCell, previousCell: MatrixStationRoleCell) => {
    if (!previousCell.hasRaceContestantTime()) {
        return;
    }
    if (!cell.hasRaceContestantTime()) {
        return;
    }

    cell.SplitTime = cell.RaceContestantTimeUsedTime! - previousCell.RaceContestantTimeUsedTime!;
};

const updateCellUsedTime = (cell: MatrixStationRoleCell, time: RaceContestantTime) => {
    cell.RaceContestantTimeUsedTime = time.AccumulatedTime;
    cell.RaceContestantTimeClockTime = time.ClockTime;
    cell.RaceContestantTimeId = time.Id;
    cell.RaceContestantTimeTUID = time.TUID;
    cell.resetBackupTime(); // We reset the backuptime since we now have a real time
    cell.resetContestantTime(); // We reset the contestanttime since we now have a real time

    // console.log(
    //     `raceContestantTime with id ${time.Id} and time ${time.ClockTime} is assigned to cell with stationRoleID: ${cell.StationRoleId}`,
    // );
};

const updateCellStartControlForRaceContestantTime = (
    row: MatrixRow,
    cell: MatrixStationRoleCell,
    time: RaceContestantTime,
    stationRole: StationRole,
) => {
    // This will never happen, we always have the stationRole if we have a RaceContestantTime
    if (!stationRole) {
        return;
    }

    // Only if the station is marked as startcontrol
    if (!stationRole.StartControl) {
        return;
    }

    // Check that we have the setting values set
    if (stationRole.StartControlMax === null || stationRole.StartControlMin === null) {
        return;
    }

    // If the RaceContestantTime is too late according to the StartControlMax, we have a start control warning
    // We use AccumulatedTime, to compare against the starttime
    if (time.AccumulatedTime < stationRole.StartControlMin * 1000 || time.AccumulatedTime > stationRole.StartControlMax * 1000) {
        cell.HasStartControlWarning = true;
        return;
    }

    // Reset incase we changed the times or something
    cell.HasStartControlWarning = false;
};

/***
 * Jumpstart warning is practically only used for Rally.
 * We need a station with GPI1Operation set to AttachAndRoundToWholeMinute, and the station must be a Neutral Zone Out.
 * If the actual time is before the rounded time, we have a jumpstart warning.
 */
const updateCellJumpStartWarning = (
    cell: MatrixStationRoleCell,
    raceContestantTime: RaceContestantTime,
    stationRole: StationRole,
    contestantTimes: ContestantTime[],
) => {
    // If its not Neutral zone out, its not a "start" station
    if (!stationRole.IsNeutralZoneOut) {
        return;
    }
    // If we arent creating a time, and rounding it we wont have any timediff
    if (stationRole.GPI1Operation !== 'AttachAndRoundToWholeMinute') {
        return;
    }

    // Find the contestantTime that matches the raceContestantTime
    const contestantTime = contestantTimes.find((ct) => ct.TUID === raceContestantTime.TUID);
    if (!contestantTime) {
        console.warn(
            `Could not find ContestantTime with TUID ${raceContestantTime.TUID} for jumpstart warning. We probably received the RaceContestantTime before the ContestantTime`,
        );
        return;
    }

    // Try to parse the time from the contestanttime
    const freeText = contestantTime.FreeText;
    if (!freeText.includes('GPIO GPIO')) {
        console.warn('We have a ContestantTime that does not contain "GPIO GPIO" when we are looking for jumpstart. FreeText:', freeText);
        return;
    }

    // The freetext will look like this:
    // "S:11580 T:1 P:GPIO GPIO 12820854236322709629 09:32:58.940"
    // or this:
    // "S:11881 T:1 P:GPIO GPIO 12820854236284836458 08:12:00.230 "

    const splits = freeText.split(' ');

    // if we dont have 5 parts, then we are missing the time part at the end and we cant do this.
    if (splits.length < 6) {
        console.warn(`FreeText does not contain enough parts to find time when looking for jumpstart warning: ${freeText}`);
        return;
    }

    const timeString = splits.at(5);

    if (!timeString) {
        console.warn(`Could not find time string in freeText when looking for jumpstart warning: ${freeText}`);
        return;
    }

    if (timeString.length !== 12) {
        console.warn(`Time string is not a valid timestamp when looking for jumpstart warning: ${timeString}`);
        return;
    }
    // TODO: We could validate more here, but now we assume the timestring is in the correct format

    try {
        const gpioTime = parseDateStringIntoDateFromFormat(
            timeString,
            getCurrentDateFormat('TIMEMS3'),
            new Date(raceContestantTime.ClockTime),
        );

        if (compareDateTimes(gpioTime, raceContestantTime.ClockTime) < 0) {
            // If the gpio time is before the raceContestantTime, we have a jumpstart
            cell.HasJumpStartWarning = true;
            cell.JumpStartTime = gpioTime;
        }
    } catch (e) {
        console.error(
            'Failed to parse GPIO time from ContestantTime FreeText when checking for jumpstart warning. FreeText:',
            freeText,
            'Error:',
            e,
        );
    }
};
